// @flow

import _ from 'lodash';

import {
  APPLICANTS_ROUTE,
  HOUSEHOLD_ROUTE,
  INCOME_ROUTE,
  LOCATION_ZIPCODE_ROUTE,
  RECOMMENDATION_ROUTE,
  ICHRA_ROUTE,
  SUBSIDY_START_ROUTE,
} from 'routes/health/constants/routes';
import { STRIDE } from 'client/constants/globals';
import { hasMissingMemberInfo } from 'client/utils/health/member';
import { SKIP } from 'routes/health/constants/index';

import type { LocationType } from 'types/location.flow';

import type { AddressType } from 'web-core/types/address';
import type { MemberType } from 'web-core/types/member';

export function setStateAsync(state: Object): Promise<*> {
  return new Promise((resolve) => {
    this.setState(state, resolve);
  });
}

export const getRedirectRoute = {
  multipleCounties: (location: LocationType): ?string => {
    if (_.isEmpty(location)) {
      return LOCATION_ZIPCODE_ROUTE;
    }
    return null;
  },
  applicants: (address: AddressType): ?string => {
    if (_.isEmpty(address)) {
      return LOCATION_ZIPCODE_ROUTE;
    }
    return null;
  },
  usageOrConditions: (members: Array<MemberType>): ?string => {
    if (hasMissingMemberInfo(members)) {
      return APPLICANTS_ROUTE;
    }
    return null;
  },
  arpa: (members: Array<Object>, isIchra): ?string => {
    if (isIchra) return ICHRA_ROUTE;
    return getRedirectRoute.usageOrConditions(members);
  },
  household: (members: Array<Object>): ?string => getRedirectRoute.usageOrConditions(members),
  split: (householdSize: number, user): ?string => {
    if (!householdSize && !(user && user.ichra)) {
      return HOUSEHOLD_ROUTE;
    }
    return null;
  },
  income: (taxInfo: Object): ?string => {
    // Check for income property as we initialize with
    // value of 0 after household selection
    if (!_.has(taxInfo, 'income')) {
      return HOUSEHOLD_ROUTE;
    }
    return null;
  },
  qualify: (income: number): ?string => {
    if (_.isNil(income)) {
      return INCOME_ROUTE;
    }
    return null;
  },
  // TODO: Update logic as we move from shortened health funnel
  account: ({ apps, user }: { apps: Array<Object>, user: Object }): ?string => {
    const healthApp = _.find(apps, { type: 'health' });
    const app_json = _.get(healthApp, 'app_json', {});
    const members = _.get(healthApp, 'members', []);
    const householdSize = _.get(app_json, 'taxInfo.familySize');

    if (_.isEmpty(members)) {
      return APPLICANTS_ROUTE;
    } else if (!householdSize && !(user && user.ichra)) {
      // Redirect to first step of subsidy since users can skip income
      // and subsidy
      return HOUSEHOLD_ROUTE;
    } else if (user.isLoggedIn) {
      return RECOMMENDATION_ROUTE;
    }
    return null;
  },
  deadline: ({
    address,
    householdSize,
    members,
    taxInfo,
    user,
  }: {
    address: AddressType,
    householdSize: number,
    members: Array<MemberType>,
    taxInfo: Object,
  }) =>
    getRedirectRoute.recommendation({
      address,
      householdSize,
      members,
      taxInfo,
      user,
    }),
  recommendation: ({
    address,
    householdSize,
    members,
    taxInfo,
    user,
  }: {
    address: AddressType,
    householdSize: number,
    members: Array<MemberType>,
    taxInfo: Object,
  }) =>
    getRedirectRoute.applicants(address) ||
    getRedirectRoute.usageOrConditions(members) ||
    getRedirectRoute.split(householdSize, user) ||
    getRedirectRoute.income(taxInfo),
  edit: ({
    householdSize,
    members,
    user,
  }: {
    householdSize: number,
    members: Array<Object>,
    user: Object,
  }): ?string => {
    if (hasMissingMemberInfo(members)) {
      return APPLICANTS_ROUTE;
    } else if (!householdSize && !(user && user.ichra)) {
      return HOUSEHOLD_ROUTE;
    }
    return null;
  },
  ichra: (state) => {
    if (_.get(state, 'siteConfigs.brandSiteConfig.partnerConfig.supportsICHRA')) {
      return null;
    }
    return SUBSIDY_START_ROUTE;
  },
};

export const getPartnerDisplayName = (state) => {
  if (_.get(state, 'siteConfigs.brandSiteConfig.partnerConfig.agencyInfo.replaceStrideName')) {
    return _.get(state, 'siteConfigs.brandSiteConfig.name', STRIDE);
  }
  return STRIDE;
};

export const getIchraContribution = state => _.get(state, 'user.ichra.contribution', null);

export const getProviderSearchRadius = state =>
  _.get(state, 'siteConfigs.brandSiteConfig.partnerConfig.agencyInfo.searchRadius', 10);

export const buttonBorderStyle = value =>
  (value !== SKIP ? 'primary' : 'transparent list-button');
